import { jwtDecode } from 'jwt-decode';
import { applySnapshot, flow, types } from 'mobx-state-tree';

import {
	IResetPasswordData,
	IResetPasswordTokenData,
	resetPassword,
	resetPasswordToken,
} from '@common/api/auth';
import backend from '@common/backend';

import { IAuthData, getAuth, removeAuth, saveAuth } from './storage';

interface IUser {
	email: string;
	exp: number;
	first_name: string;
	iat: number;
	iss: string;
	jti: string;
	last_name: string;
	nbf: number;
	permissions: any[]; // @TODO: type
	prv: string;
	roles: any[]; // @TODO: import role type
	sub: string;
}

const authData = getAuth();

const impersonateRoleKey = 'impersonateRole';

const AuthInterfaceModel = types
	.model('AuthInterfaceModel', {
		token: types.optional(types.string, authData.token),
		centrifugo_channel: types.optional(types.string, authData.centrifugo_channel),
		impersonateRole: types.optional(types.string, localStorage.getItem(impersonateRoleKey) || ''),
	})
	.volatile(() => ({
		loading: false,
		preloading: true,
	}))
	.views((self) => ({
		get isAuthorized() {
			return !!self.token;
		},
		get user(): IUser {
			return self.token ? jwtDecode(self.token) : null;
		},
	}))
	.views((self) => ({
		get fullName() {
			return `${self.user.first_name} ${self.user?.last_name || ''}`;
		},
		get nameInitials() {
			return `${self.user.first_name[0]}${self.user?.last_name?.[0] || ''}`;
		},
		get activeRole() {
			return self.impersonateRole || self.user.roles?.[0]?.name || '';
		},
	}))
	.views((self) => ({
		get isMentorView() {
			return self.activeRole === 'Mentor/Tutor';
		},
	}))
	.actions((self) => ({
		setImpersonateRole(role: string) {
			self.impersonateRole = role;
			localStorage.setItem(impersonateRoleKey, role);
		},
		clearImpersonateRole() {
			self.impersonateRole = '';
			localStorage.removeItem(impersonateRoleKey);
		},
	}))
	.actions((self) => ({
		_login: flow(function* (data: IAuthData, isSave: boolean = false) {
			yield backend.login(data.token, data.centrifugo_channel);
			applySnapshot(self, data);
			isSave && saveAuth(data);

			const defaultRole = jwtDecode<IUser>(data.token).roles?.[0]?.name || '';
			self.setImpersonateRole(defaultRole);
		}),
		logout() {
			applySnapshot(self, {
				token: '',
				centrifugo_channel: '',
			});
			removeAuth();
			backend.logout();

			self.clearImpersonateRole();
		},
		resetPassword: flow(function* (data: IResetPasswordData) {
			self.loading = true;
			try {
				const result = yield resetPassword(data);
				return result.token;
			} finally {
				self.loading = false;
			}
		}),
		resetPasswordToken: flow(function* (token: string, data: IResetPasswordTokenData) {
			self.loading = true;
			try {
				yield resetPasswordToken(token, data);
			} finally {
				self.loading = false;
			}
		}),
	}))
	.actions((self) => ({
		afterCreate: flow(function* () {
			self.preloading = true;
			if (self.isAuthorized) {
				yield backend.login(self.token, self.centrifugo_channel);
			}
			self.preloading = false;
		}),
		checkToken: () => {
			const auth = getAuth();
			applySnapshot(self, auth);
		},
	}));

export default AuthInterfaceModel;
